<template>
  <div
    class="flex mb-1 items-center flex-wrap w-auto animate-[slide_0.3s_ease-out]"
    v-if="filterMenuOpen && !isLiveView"
  >
    <div class="mx-1 gap-2 my-1 flex-1 min-w-[200px]">
      <input
        v-model="userSearch"
        type="text"
        class="w-full px-3 py-1.5 text-sm text-gray-900 bg-white border border-gray-300 rounded focus:outline-none focus:border-[#386f99] focus:ring-1 focus:ring-[#386f99]"
        placeholder="Search User ID"
      />
    </div>

    <div
      class="date-range-search mx-1 my-1 flex items-center gap-2 flex-1 min-w-[280px]"
    >
      <flat-pickr
        v-model="dateStart"
        :config="{
          enableTime: false,
          dateFormat: 'Y-m-d',
          maxDate: dateEnd || null,
        }"
        class="w-full px-3 py-1.5 text-sm text-gray-900 bg-white border border-gray-300 rounded focus:outline-none focus:border-[#386f99]"
        placeholder="Start Date"
      />

      <span class="text-gray-500 font-medium">to</span>

      <flat-pickr
        v-model="dateEnd"
        :config="{
          enableTime: false,
          dateFormat: 'Y-m-d',
          minDate: dateStart || null,
        }"
        class="w-full px-3 py-1.5 text-sm text-gray-900 bg-white border border-gray-300 rounded focus:outline-none focus:border-[#386f99]"
        placeholder="End Date"
      />
    </div>

    <div class="flex items-center flex-1 my-1 mx-1 gap-2 min-w-[180px]">
      <button
        class="btn-sm flex justify-center items-center text-center flex-1 rounded-[10px] bg-green-600 hover:bg-green-700 text-white text-sm py-1.5 px-3 transition-colors duration-200"
        @click="searchByDateRange"
      >
        Search
        <i class="fa-solid ms-2 fa-magnifying-glass"></i>
      </button>
      <button
        class="btn-sm flex justify-center items-center text-center flex-1 rounded-[10px] bg-red-600 hover:bg-red-700 text-white text-sm py-1.5 px-3 transition-colors duration-200"
        @click="resetFilter"
      >
        Reset
        <i class="fa-solid fa-eraser ms-1 text-white"></i>
      </button>
    </div>
  </div>

  <div
    class="grid grid-cols-1 lg:grid-cols-12 gap-0 min-h-screen h-auto w-full bg-white border border-gray-200 rounded-xl overflow-hidden"
  >
    <div
      class="lg:col-span-7 flex flex-col items-stretch justify-start border-r border-gray-200 bg-white"
    >
      <div
        class="bg-[#386f99] text-white font-bold p-3 flex items-center justify-between text-sm sm:text-base"
      >
        <div class="truncate mr-2">CHAT (Skin: {{ skinFilt }})</div>
        <div class="flex items-center gap-2 flex-shrink-0">
          <button
            class="px-3 py-1 rounded-full text-xs font-semibold transition-all duration-200 flex items-center bg-black/20 text-white/80"
            :class="{ '!bg-green-500 border border-white  !text-white shadow-sm': isLiveView }"
            @click="isLiveView = true"
          >
            Live
            <i class="fa-solid text-white fa-bolt ms-1.5"></i>
          </button>
          <button
            class="px-3 py-1 rounded-full text-xs font-semibold transition-all duration-200 flex items-center bg-black/80 text-white/80"
            :class="{ '!bg-gray-600 border border-white !text-white shadow-sm': !isLiveView }"
            @click="isLiveView = false"
          >
            History
            <i class="fa-solid text-white fa-clock-rotate-left ms-1.5"></i>
          </button>
          <div class="flex items-center gap-2.5 ms-1" v-if="!isLiveView">
            <i
              @click="showFilter"
              class="fa-solid text-white cursor-pointer hover:opacity-80 transition-opacity fa-filter"
            ></i>
            <i
              @click="refreshData"
              class="fa-solid fa-arrows-rotate cursor-pointer hover:opacity-80 transition-opacity text-white"
            ></i>
          </div>
        </div>
      </div>

      <div
        class="flex flex-col flex-1"
        :class="messages.length > 0 ? 'justify-between' : 'justify-center'"
        v-if="!isLoading"
      >
        <div
          v-if="messages.length > 0"
          class="divide-y divide-gray-100 max-h-[calc(100vh-140px)] overflow-y-auto"
        >
          <div
            v-for="msg in messages"
            :key="msg._id"
            class="w-full transition-all duration-200 border-b border-gray-100"
            :class="
              messageDetail?.data?._id === msg?._id
                ? 'bg-gray-100'
                : 'bg-white hover:bg-gray-50/80'
            "
          >
            <div class="flex items-start p-1">
              <img
                :src="`${this.avatarPrefix}${msg.avatar
                  ?.toString()
                  .replace('.png', '')}.png`"
                @error="handleImageError($event)"
                alt="avatar"
                class="rounded-circle mr-3 flex-shrink-0 object-cover"
                width="35"
                height="35"
              />
              <div
                class="flex flex-col items-start justify-center w-full cursor-pointer"
                @click="openDetail(msg._id)"
              >
                <div class="flex justify-between items-center w-full mb-1">
                  <strong
                    class="text-[#386f99] text-sm font-bold truncate max-w-[180px] sm:max-w-[250px]"
                  >
                    {{ msg.user_id }}
                  </strong>
                  <strong
                    class="text-xs text-emerald-600  whitespace-nowrap ml-2"
                  >
                    {{ formatDate(msg.created_at) }}
                  </strong>
                </div>

                <div v-if="msg.type === 'photo'" class="mt-1 max-w-[200px]">
                  <img
                    :src="msg.url"
                    alt="GIF/Photo"
                    class="w-full h-auto rounded border border-gray-200"
                  />
                </div>
                <div
                  v-else
                  class="text-black text-[14px] leading-relaxed break-words whitespace-pre-wrap pr-2 text-left"
                >
                  {{ msg.message }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          class="p-8 text-center text-gray-500 font-medium italic flex-1 flex items-center justify-center"
          v-else
        >
          No messages found.
        </div>

        <div
          v-if="isLoadingNewPage"
          class="p-4 flex justify-center items-center w-full border-t border-gray-100 bg-gray-50"
        >
          <div
            class="w-6 h-6 border-2 border-[#386f99] border-t-transparent rounded-full animate-spin"
          ></div>
        </div>

        <button
          :disabled="isLoadingNewPage"
          v-if="currentPage < totalPages && !isLiveView"
          @click="loadMoreMessage"
          class="w-full py-2.5 bg-gray-50 text-gray-700 font-medium text-sm border-t border-gray-200 hover:bg-gray-100 active:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Load More
        </button>

        <div
          class="flex items-stretch border-t border-gray-200 p-2 bg-gray-50 gap-2"
          v-if="isLiveView"
        >
          <input
            type="text"
            placeholder="Type message..."
            v-model="adminMessage"
            class="flex-1 px-4 py-2 text-sm text-black bg-white border border-gray-300 rounded focus:outline-none focus:border-[#386f99]"
            @keydown.enter="sendMessage"
          />
          <button
            :disabled="isLoadingNewPage"
            @click="sendMessage"
            class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded transition-colors flex items-center gap-1.5 disabled:opacity-60"
          >
            <template v-if="!isSendingMsg">
              Send<i class="fa-solid fa-paper-plane text-xs"></i>
            </template>
            <span v-else class="flex items-center justify-center">
              <div
                class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
              ></div>
            </span>
          </button>

          <div
            class="flex items-center px-3 bg-[#386f99]/10 text-[#386f99] rounded border border-[#386f99]/20 font-semibold text-xs whitespace-nowrap"
          >
            <i class="fa-solid fa-users mr-1.5"></i>{{ livePlayerCounter }}
          </div>
        </div>
      </div>

      <div
        class="flex justify-center items-center w-full flex-1 min-h-[300px]"
        v-else-if="isLoading"
      >
        <div
          class="w-8 h-8 border-3 border-[#386f99] border-t-transparent rounded-full animate-spin"
        ></div>
      </div>
    </div>

    <div
      class="lg:col-span-5 flex flex-col bg-[#fff] text-black relative"
      :class="messageDetail?.data?._id === msg?._id ? 'block' : 'block'"
    >
      <div class="flex flex-col h-full w-full">
        <div
          v-if="messageDetail"
          class="bg-[#386f99] p-3 border-b border-white/[0.08] flex items-center justify-between font-bold text-white text-sm sm:text-base"
        >
          <span class="truncate">Selected message Info</span>
          <button
            class="w-5 h-5 flex flex-col justify-center items-center relative group opacity-70 hover:opacity-100 transition-opacity"
            @click="closeModal"
          >
            <span class="w-4 h-0.5 bg-white rotate-45 absolute"></span>
            <span class="w-4 h-0.5 bg-white -rotate-45 absolute"></span>
          </button>
        </div>

        <div
          class="flex flex-col flex-1 p-2 overflow-y-auto"
          :class="
            !messageDetail
              ? 'justify-content-center items-center'
              : 'justify-start'
          "
        >
          <div
            v-if="messageDetail && !isLoadingDetail"
            class="w-full space-y-4 bg-white p-2 rounded-lg"
          >
            <div
              class="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2.5"
            >
              <div class="flex justify-between items-center text-sm">
                <strong class="text-gray-700">User ID:</strong>
                <a
                  href="#"
                  class="text-[#386f99] hover:underline font-bold "
                >
                  {{ messageDetail.data.user_id }}
                </a>
              </div>

              <div
                class="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pt-2 border-t border-gray-200"
              >
                <div class="flex justify-between items-center flex-1 text-sm">
                  <strong class="text-gray-700">Total Messages:</strong>
                  <span class="text-gray-900 font-medium">
                    {{ messageDetail.totalMessage }}
                    <span class="text-gray-500 text-xs"
                      >(Today: {{ messageDetail.messageToday }})</span
                    >
                  </span>
                </div>
                <button
                  class="bg-red-600 hover:bg-red-700 text-white text-xs py-1 px-2.5 rounded flex items-center justify-center gap-1.5 transition-colors self-end sm:self-auto shadow-sm"
                  @click="
                    deleteAllMessageFromPlayer(messageDetail.data.user_id)
                  "
                >
                  <i class="fa-solid fa-trash text-[10px]"></i>
                  Delete All
                </button>
              </div>
            </div>

            <div
              class="flex items-center justify-between text-xs bg-gray-50 px-3 py-2 rounded border border-gray-100"
            >
              <strong class="text-gray-600">Created At:</strong>
              <span class=" text-emerald-700 font-semibold">
                {{ formatDate(messageDetail.data.created_at) }}
              </span>
            </div>

            <div
              class="bg-gray-50 rounded-lg border border-gray-200 p-3 space-y-2"
            >
              <strong
                class="text-xs text-gray-500 uppercase tracking-wider block text-start font-bold"
                >Message Content:</strong
              >

              <div
                v-if="messageDetail.data.type === 'photo'"
                class="max-w-[250px] mx-auto pt-1"
              >
                <img
                  :src="messageDetail.data.url"
                  alt="GIF/Photo"
                  class="w-full h-auto rounded border border-gray-300"
                />
              </div>
              <div
                v-else
                class="text-black text-sm bg-white p-2.5 rounded border border-gray-200 whitespace-pre-wrap text-start shadow-inner"
              >
                {{ messageDetail.data.message }}
              </div>

              <div class="text-end pt-1">
                <button
                  @click="deleteMessage(messageDetail.data._id)"
                  class="bg-red-600 hover:bg-red-700 text-white text-xs py-1 px-3 rounded inline-flex items-center gap-1.5 transition-colors shadow-sm"
                >
                  <i class="fa-solid fa-trash text-[10px]"></i>
                  Delete
                </button>
              </div>
            </div>

            <div
              class="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1.5 text-start"
              v-if="messageDetail.player !== null"
            >
              <span class="block text-amber-800 font-bold text-sm">
                Active ban present for this player
              </span>
              <span class="block text-gray-600 text-xs">
                Creating a new ban will automatically deactivate previous
                records.
              </span>

              <div
                class="bg-white p-2 rounded text-xs mt-2 space-y-1 text-gray-800 border border-amber-100 shadow-sm"
              >
                <p>
                  <strong class="text-gray-700">Reason:</strong>
                  {{ messageDetail.player.reason }}
                </p>
                <p
                  v-if="messageDetail.player.reason === 'Other'"
                  class="break-words"
                >
                  <strong class="text-gray-700">Note:</strong>
                  <span class="text-amber-900 font-medium">{{
                    messageDetail.player.note
                  }}</span>
                </p>
                <p>
                  <strong class="text-gray-700">Blocked until:</strong>
                  <span class="text-emerald-700  font-semibold">{{
                    formatDate(messageDetail.player.end_at)
                  }}</span>
                </p>
              </div>
            </div>

            <hr class="border-gray-200" />

            <div class="space-y-3">
              <h6 class="font-bold text-gray-800 text-sm text-start">
                Block User Control Panel
              </h6>
              <div class="grid grid-cols-2 gap-3 items-end">
                <div class="space-y-1.5 text-start">
                  <label class="block text-xs text-gray-600 font-medium">
                    <i class="fa-solid text-gray-400 fa-clock mr-1"></i>Block
                    time (mins)
                  </label>
                  <input
                    v-model="blockTime"
                    type="number"
                    class="w-full bg-white border border-gray-300 rounded px-2.5 py-1.5 text-xs text-black placeholder:text-gray-400 focus:outline-none focus:border-[#386f99] focus:ring-1 focus:ring-[#386f99]"
                    placeholder="0"
                  />
                </div>

                <div class="space-y-1.5 text-start">
                  <label class="block text-xs text-gray-600 font-medium"
                    >Reason</label
                  >
                  <select
                    v-model="blockReason"
                    class="w-full bg-white border border-gray-300 rounded px-2 py-1.5 text-xs text-black focus:outline-none focus:border-[#386f99] focus:ring-1 focus:ring-[#386f99]"
                    @change="onBlockReasonChange"
                  >
                    <option
                      v-for="reason in reasons"
                      :key="reason"
                      :value="reason"
                    >
                      {{ reason }}
                    </option>
                  </select>
                </div>

                <div class="col-span-2 text-start">
                  <small class="text-gray-500 text-[11px]">
                    For permanent/unlimited block, specify <b>0</b>
                  </small>
                </div>

                <div
                  v-if="blockReason === 'Other'"
                  class="col-span-2 text-start"
                >
                  <textarea
                    v-model="customNote"
                    class="w-full h-16 bg-white border border-gray-300 rounded p-2 text-xs text-black placeholder:text-gray-400 focus:outline-none focus:border-[#386f99] focus:ring-1 focus:ring-[#386f99] resize-none"
                    placeholder="Specify reasoning detailed info..."
                  ></textarea>
                </div>

                <div class="col-span-2 flex justify-center pt-1">
                  <button
                    class="bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-4 py-2 rounded flex items-center gap-1.5 transition-colors shadow-md"
                    @click="handleBannedPlayer(messageDetail.data.user_id)"
                  >
                    <i class="fa-solid fa-ban text-[11px]"></i>
                    Ban user
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div
            class="flex justify-center items-center w-full flex-1 min-h-[200px]"
            v-else-if="isLoadingDetail"
          >
            <Loader />
          </div>

          <div
            v-else
            class="flex justify-center items-center w-full flex-1 text-black text-sm font-medium italic min-h-[300px]"
          >
            Select a message to view details
          </div>
        </div>
      </div>
    </div>
  </div>

  <ExportData :skinFilt="skinFilt" :tableName="'messages'" />
</template>

<script>
import Swal from "sweetalert2";
import { mapGetters } from "vuex";
import { getFetch, postFetch, deleteRequest } from "@/api/axiosClient";
import Loader from "@/components/Loader.vue";
import socketIO from "socket.io-client";
import msgpack from "socket.io-msgpack-parser";
import ExportData from "@/slot/components/table/ExportData.vue";
import FlatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

export default {
  name: "MessagesManager",
  components: {
    ExportData,
    Loader,
    FlatPickr,
  },
  computed: {
    ...mapGetters(["isMenuOpen"]),
  },
  props: {
    skinFilt: {
      type: Number,
      required: true,
      default: 0,
    },
    category: {
      type: String,
      default: "slot",
    },
    database: {
      type: String,
      default: "theearcasino",
    },
  },
  data() {
    const today = new Date();
    const endDate = today.toISOString().split("T")[0];
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);
    const startDate = sevenDaysAgo.toISOString().split("T")[0];

    return {
      isLiveView: true,
      mainSocket: null,
      livePlayerCounter: 0,
      isConnectingToSocket: true,
      messages: [],
      isLoading: false,
      isLoadingDetail: false,
      currentPage: 1,
      selectedLimit: 12,
      totalPages: 1,
      isSendingMsg: false,
      messageDetail: null,
      reasons: ["Spam", "Offensive", "Flood", "Other"],
      blockReason: "Spam",
      blockTime: 0,
      isLoadingNewPage: false,
      userSearch: null,
      customNote: "",
      dateStart: startDate,
      dateEnd: endDate,
      filterMenuOpen: true,
      adminMessage: null,
      avatarPrefix:
        "https://storage.googleapis.com/camasy-images/avataraugustus/avatar_",
    };
  },
  watch: {
    category: {
      async handler(newCat, oldCat) {
        if (newCat !== oldCat && this.isLiveView) {
          if (this.mainSocket) {
            this.mainSocket.disconnect();
            this.mainSocket = null;
          }
          await this.connectToSocket();
          await this.fetchMessagesFromSocket();
        }
      },
    },
    skinFilt: {
      async handler(newSkin, oldSkin) {
        if (newSkin !== oldSkin && this.isLiveView) {
          if (this.mainSocket) {
            this.mainSocket.disconnect();
            this.mainSocket = null;
          }
          await this.connectToSocket();
          await this.fetchMessagesFromSocket();
        }
      },
    },
    isLiveView(newVal) {
      this.messages = [];
      this.messageDetail = null;
      this.currentPage = 1;
      if (newVal) {
        if (!this.mainSocket) {
          this.connectToSocket();
        }
        this.fetchMessagesFromSocket();
      } else {
        this.resetFilter();
      }
    },
  },
  methods: {
    getSocketUrl() {
      const cat = (this.category || "slot").toLowerCase();
      if (cat === "livecasino" || cat === "live_casino" || cat === "roulette") {
        return (
          process.env.VUE_APP_URL_WSS_LIVECASINO ||
          process.env.VUE_APP_URL_WSS ||
          "https://stage-wss.theearplatform.com"
        );
      }
      if (cat === "instant") {
        return (
          process.env.VUE_APP_URL_WSS_INSTANT ||
          process.env.VUE_APP_URL_WSS ||
          "https://stage-wss.theearplatform.com"
        );
      }
      return (
        process.env.VUE_APP_URL_WSS ||
        "https://stage-wss.theearplatform.com"
      );
    },
    getSocketPath() {
      const cat = (this.category || "slot").toLowerCase();
      if (cat === "livecasino" || cat === "live_casino" || cat === "roulette") {
        return (
          process.env.VUE_APP_PATH_WSS_LIVECASINO ||
          process.env.VUE_APP_PATH_WSS_LIVECASINOS ||
          `${process.env.VUE_APP_PATH_WSS || "wsstheearcasino"}livecasino`
        );
      }
      if (cat === "instant") {
        return (
          process.env.VUE_APP_PATH_WSS_INSTANT ||
          `${process.env.VUE_APP_PATH_WSS || "wsstheearcasino"}instant`
        );
      }
      return (
        process.env.VUE_APP_PATH_WSS ||
        "wsstheearcasino"
      );
    },
    onBlockReasonChange() {
      if (this.blockReason !== "Other") {
        this.customNote = "";
      }
    },
    closeModal() {
      this.messageDetail = null;
    },
    async connectToSocket() {
      const wss = this.getSocketUrl();

      const newPath = this.getSocketPath();

      this.mainSocket = socketIO(wss, {
        reconnectionDelay: 3000,
        parser: msgpack,
        path: "/" + newPath,
        reconnectionDelayMax: 30000,
        transports: ["websocket"],
        auth: {
          username: "crashgames",
          password: "crashgames",
          userId: `${this.skinFilt}_moderation`,
          skinId: parseInt(this.skinFilt),
          game_id: "allgames",
        },
        pingInterval: 10000,
        pingTimeout: 15000,
      });
      if (this.mainSocket) {
        this.mainSocket.on("connect", () => {
          this.setIsConnecting(false);
        });

        this.mainSocket.on("disconnect", () => {
          this.setIsConnecting(true);
        });

        this.mainSocket.on("updateMessage", (data) => {});

        this.mainSocket.on("counter", (data) => {
          this.livePlayerCounter = parseInt(data.counter);
        });

        this.mainSocket.on("receiveMessage", (data) => {
          this.fetchMessagesFromSocket();
        });
      }
    },
    setIsConnecting(val) {
      this.isConnectingToSocket = val;
    },
    async fetchMessagesFromSocket() {
      if (this.mainSocket) {
        this.messages = [];
        this.messageDetail = null;
        this.isLoading = true;
        this.mainSocket.emit(
          "getLatestMessages",
          {
            currency_code: "",
            skin_id: parseInt(this.skinFilt),
            hiderain: true,
          },
          (data) => {
            this.messages = data?.slice(0, 100) || [];
            this.isLoading = false;
          },
        );
      }
    },
    refreshData() {
      this.currentPage = 1;
      this.fetchMessages();
    },
    showFilter() {
      this.filterMenuOpen = !this.filterMenuOpen;
    },
    resetFilter() {
      const today = new Date();
      const endDate = today.toISOString().split("T")[0];
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(today.getDate() - 7);
      const startDate = sevenDaysAgo.toISOString().split("T")[0];
      this.dateStart = startDate;
      this.dateEnd = endDate;
      this.userSearch = null;
      this.currentPage = 1;
      this.fetchMessages();
    },
    async searchByDateRange() {
      if (!this.dateStart || !this.dateEnd) {
        Swal.fire(
          "Missing dates",
          "Please select both start and end dates.",
          "warning",
        );
        return;
      }

      const start = new Date(this.dateStart);
      const end = new Date(this.dateEnd);

      if (start > end) {
        Swal.fire(
          "Invalid range",
          "Start date must be before or equal to end date.",
          "error",
        );
        return;
      }

      this.currentPage = 1;
      this.fetchMessages();
    },

    async fetchMessages(append = false) {
      this.isLoading = !append ? true : false;
      this.isLoadingNewPage = append ? true : false;
      const response = await getFetch(
        `messages`,
        {
          page: this.currentPage,
          limit: this.selectedLimit,
          skin_id: this.skinFilt,
          start_date: this.dateStart
            ? new Date(this.dateStart).toISOString()
            : null,
          end_date: this.dateEnd ? new Date(this.dateEnd).toISOString() : null,
          player_id: this.playerIdFilter || null,
          text: this.textFilter || null,
          room: this.selectedRoom === "all" ? null : this.selectedRoom,
        },
        true,
        this.database
      );

      if (append) {
        this.messages = [...this.messages, ...response.data];
      } else {
        this.messages = response.data;
      }

      this.totalPages = response.totalPages ?? 1;
      this.isLoading = false;
      this.isLoadingNewPage = false;
    },
    async loadMoreMessage() {
      this.currentPage = this.currentPage + 1;
      this.fetchMessages(true);
    },
    async sendMessage() {
      if (
        this.adminMessage !== null &&
        this.adminMessage !== "" &&
        this.adminMessage !== undefined
      ) {
        this.isSendingMsg = true;

        const messagePayload = {
          skin_id: parseInt(this.skinFilt),
          user_id: `${this.skinFilt}_nino_moderation`,
          username: `${this.skinFilt}_nino_moderation`,
          message: this.adminMessage,
          avatar: "9",
          type: "text",
        };

        try {
          this.mainSocket.emit("sendMessage", messagePayload, (response) => {
            this.adminMessage = "";

            this.isSendingMsg = false;

            if (!this.isLiveView) {
              this.currentPage = 1;
              this.messageDetail = null;
              this.fetchMessages();
            }
          });
        } catch (error) {
          console.error("Socket error while sending message:", error);
          Swal.fire("Error", "Failed to send message via socket.", "error");
          this.isSendingMsg = false;
        }
      }
    },
    // async sendMessage() {
    //   if (
    //     this.adminMessage !== null &&
    //     this.adminMessage !== "" &&
    //     this.adminMessage !== undefined
    //   ) {
    //     this.isSendingMsg = true;

    //     mainSocket.emit(
    //       "sendMessage",
    //       {
    //         skin_id: parseInt(this.skinFilt),
    //         user_id: `${this.skinFilt}_nino_moderation`,
    //         username: `${this.skinFilt}_nino_moderation`,
    //         message: this.adminMessage,
    //         avatar:
    //           "https://storage.googleapis.com/camasy-images/avataraugustus/avatar_9.png",
    //         type: "text",
    //       },
    //       (response) => {}
    //     );

    //     try {
    //       let body = {
    //         skin_id: parseInt(this.skinFilt),
    //         user_id: `${this.skinFilt}_nino_moderation`,
    //         username: `${this.skinFilt}_nino_moderation`,
    //         message: this.adminMessage,
    //         avatar:
    //           "https://storage.googleapis.com/camasy-images/avataraugustus/avatar_9.png",
    //         type: "text",
    //       };

    //       const result = await postFetch("/messages/createMessage", body);
    //       if (result?.status === 200) {
    //         this.adminMessage = "";
    //       }
    //     } catch (error) {
    //       console.error("Error sending message:", error);
    //       Swal.fire(
    //         "Error",
    //         "Something went wrong while sending the message.",
    //         "error"
    //       );
    //     } finally {
    //       this.isSendingMsg = false;
    //       this.currentPage = 1;
    //       this.messageDetail = null;
    //       this.fetchMessages();
    //     }
    //   }
    // },
    async deleteMessage(id) {
      const result = await Swal.fire({
        title: this.$t("warm.messageDeleteTitle"),
        text: this.$t("warm.messageDeleteText"),
        icon: "warning",
        heightAuto: false,
        showCancelButton: true,
        confirmButtonText: this.$t("warm.messageDeleteConfirm"),
        cancelButtonText: this.$t("util.cancel"),
        customClass: {
          popup: "delete-popup",
        },
      });

      if (result.isConfirmed) {
        try {
          await deleteRequest(`messages/${id}`);

          this.messages = this.messages.filter((message) => message._id !== id);

          await Swal.fire(this.$t("warm.deleted"), this.$t("warm.deletedText"));
        } catch (error) {
          console.error("Errore nell'eliminazione del messaggio:", error);

          await Swal.fire(
            this.$t("warm.error"),
            this.$t("warm.messageDeletedError"),
            "error",
          );
        } finally {
          this.messageDetail = null;
        }
      }
    },
    async deleteAllMessageFromPlayer(user_id) {
      const result = await Swal.fire({
        title: this.$t("warm.messageDeleteTitle"),
        text: this.$t("warm.messageDeleteText"),
        icon: "warning",
        heightAuto: false,
        showCancelButton: true,
        confirmButtonText: this.$t("warm.messageDeleteConfirm"),
        cancelButtonText: this.$t("util.cancel"),
        customClass: {
          popup: "delete-popup",
        },
      });

      if (result.isConfirmed) {
        try {
          await deleteRequest(`messages/deleteallmessage/${user_id}`);

          this.messages = this.messages.filter(
            (message) => message.user_id !== user_id,
          );

          await Swal.fire(this.$t("warm.deleted"), this.$t("warm.deletedText"));
        } catch (error) {
          console.error("Errore nell'eliminazione del messaggio:", error);

          await Swal.fire(
            this.$t("warm.error"),
            this.$t("warm.messageDeletedError"),
            "error",
          );
        } finally {
          this.messageDetail = null;
        }
      }
    },
    async handleBannedPlayer(user_id) {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "This action will prevent the player from interacting with the chat.",
        icon: "warning",
        heightAuto: false,
        showCancelButton: true,
        confirmButtonText: "Yes, ban it!",
        cancelButtonText: "Cancel",
      });

      if (!result.isConfirmed) return;

      try {
        const note = this.blockReason === "Other" ? this.customNote : "";
        const reason = this.blockReason;
        const block_minutes = Number(this.blockTime) || 0;
        const skin_id = this.skinFilt;

        if (reason === "Other" && note === "") {
          await Swal.fire({
            title: "Missing reason",
            text: "Please specify a reason before proceeding.",
            icon: "warning",
            heightAuto: false,
          });
          return;
        }

        await postFetch(`/messagebanned/${user_id}`, {
          is_banned_chat: true,
          reason,
          skin_id,
          user_id,
          note,
          is_active: true,
          block_minutes,
        });

        Swal.fire({
          title: "Banned!",
          text: "The player has been banned.",
          heightAuto: false,
        });
      } catch (error) {
        console.error("Error Update Player:", error);
        Swal.fire({
          title: "Something went wrong...",
          text: "Failed to ban the player.",
          icon: "error",
          heightAuto: false,
        });
      } finally {
        this.blockReason = "Spam";
        this.customNote = "";
        this.blockTime = 0;
        this.openDetail(this.messageDetail?.data?._id, true);
      }
    },
    handleImageError(event) {
      event.target.src = `${this.avatarPrefix}9.png`;
    },
    async openDetail(id, isRefreshAfterUpdate = false) {
      if (id === this.messageDetail?.data?._id && !isRefreshAfterUpdate) {
        this.messageDetail = null;
        return;
      }

      this.isLoadingDetail = true;
      const response = await getFetch(`messages/${id}`, {}, true, this.database);
      if (!response || !response.data) {
        return;
      }
      this.messageDetail = response;

      this.isLoadingDetail = false;
    },
    formatDate(dateStr, isShortVersion = false) {
      if (dateStr === "2999-12-31T23:59:59.999Z") {
        return "Forever";
      }

      const date = new Date(dateStr);

      if (isNaN(date.getTime())) {
        return "Invalid date";
      }

      if (isShortVersion) {
        return date.toISOString().slice(0, 10).split("-").reverse().join("/");
      }

      const day = String(date.getUTCDate()).padStart(2, "0");
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");
      const year = date.getUTCFullYear();

      const hours = String(date.getUTCHours()).padStart(2, "0");
      const minutes = String(date.getUTCMinutes()).padStart(2, "0");
      const seconds = String(date.getUTCSeconds()).padStart(2, "0");

      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds} UTC`;
    },
  },
  async mounted() {
    if (this.isLiveView) {
      await this.connectToSocket();
      await this.fetchMessagesFromSocket();
    } else {
      this.fetchMessages();
    }
  },
  beforeUnmount() {
    if (this.mainSocket) {
      this.mainSocket.disconnect();
      this.mainSocket = null;
    }
  },
};
</script>

<style lang="scss" scoped>
.min-h-100 {
  min-height: 100%;
}

.messages-manager-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  height: 100%;
  width: 100%;
  position: relative;
  padding-top: 0rem;

  .last-message-section,
  .detail-messages-container {
    color: white;
    text-align: center;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  .last-message-section {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    height: 100%;
    width: 60%;
    // border: 2px solid rgba(255, 255, 255, 0.1019607843);
    border-right: 1px solid rgba(255, 255, 255, 0.1019607843);
    border-radius: 0px;
    overflow: hidden;

    .heading-live-message {
      padding: 0.5rem;
      width: 100%;
      border-bottom: 1px solid rgba(255, 255, 255, 0.514);
      //background: #8080804f;
      //background: #1d4ed8;
      background: linear-gradient(45deg, #2196f3, #1976d2);
      border-color: transparent;
      box-shadow: 0 4px 15px rgba(33, 150, 243, 0.3);
      color: #fff;
      display: flex;
      justify-content: flex-start;
      align-items: center;
    }

    @media (max-width: 991px) {
      width: 100%;
      max-width: 100%;
    }
  }

  .detail-message-section {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    height: 100%;
    width: 40%;
    // border: 2px solid rgba(255, 255, 255, 0.1019607843);
    border: 0px solid rgba(255, 255, 255, 0.1019607843);
    border-radius: 0px;
    overflow: hidden;
    .detail-messages-container {
      width: 100%;

      @media (max-width: 991px) {
        width: 90%;
        margin: 0 auto;
        max-width: 500px;
        height: 90%;
        background: linear-gradient(145deg, #1a2942, #0f1825);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 10px;
        max-height: fit-content;
        overflow: hidden;
      }
    }
    @media (max-width: 991px) {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #0000008a;
      z-index: 9876543219;
      overflow-y: scroll;
      scrollbar-width: none;
    }
  }
}

.show-hide-overlay {
  @media (max-width: 991px) {
    opacity: 0;
    visibility: hidden;
  }
}

.heading-selected-message {
  padding: 0.5rem;
  width: 100%;
  border-bottom: 1px solid rgba(255, 255, 255, 0.514);
  // background: #8080804f;
  // background: #1d4ed8;
  background: linear-gradient(45deg, #2196f3, #1976d2);
  border-color: transparent;
  box-shadow: 0 4px 15px rgba(33, 150, 243, 0.3);
  color: #fff;
  display: flex;
  justify-content: flex-start;
  align-items: center;
}

.message-detail-content,
.last-messages-container {
  padding: 0;
  min-height: 700px;
  max-height: 700px;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #505075 transparent;

  @media (max-width: 767px) {
    min-height: 35vh;
  }
}

.message-detail-content {
  @media (max-width: 991px) {
    min-height: 100%;
    height: 100%;
    overflow-x: hidden;
    overflow-y: auto;
    padding-bottom: 55px;
    max-height: 100%;
  }
}

.message-photo {
  overflow: hidden;
  width: 100%;
  height: 100%;
  max-width: 100px;
  max-height: 100px;

  .img-view {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.message-text {
  text-align: start;
  white-space: nowrap;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  max-width: calc(100% - 55px);
}

.full-list {
  max-width: calc(100% - 85px);
}

.timestamp-sms-detail {
  color: rgb(74 222 128);
  padding: 0.5rem;
  display: flex;
  align-items: center;
  font-weight: 500;
}

.timestamp-sms {
  color: rgb(74 222 128);
  font-weight: 500;
  text-align: right;
  width: 90%;
  max-width: 90%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.message-box-wrapper {
  padding: 0.35rem 0.55rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  border-top: 1px solid transparent;
  border-left: 1px solid transparent;
  border-right: 1px solid transparent;
}

.message-content {
  // background: rgba(255, 255, 255, 0.05);
  // border: 2px solid rgba(255, 255, 255, 0.171) ;
  border-radius: 10px;
  position: relative;
  font-size: 90%;
  cursor: pointer;
  // background: linear-gradient(135deg, #2e2e35 0%, #27272e 100%);
  // border-radius: 14px;
  // border: 1px solid rgba(255, 255, 255, 0.06);
  // box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.04);
}

.content-message {
  overflow-x: hidden;
  overflow-y: auto;
  flex: 1;
}

.content-message-detail {
  // background: linear-gradient(135deg, #2e2e35 0%, #27272e 100%);
  background: rgb(47 64 91);
  border-radius: 14px;
  border: 1px solid #cccccc50;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.04);
  border-radius: 14px;
}

.select-custom {
  line-height: 2;
  min-height: 40px;
  height: 40px;
}

.active-msg {
  border: 2px solid rgb(74 222 128);
  // border-radius: 5px;
  // padding: 0px 0.35rem;
}

.custom-button-style {
  border-radius: 10px;
  padding: 0.55rem 0.95rem;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    filter: brightness(0.8);
  }
}

.btn-warning {
  background-color: #6f6f3f;
  background: #6f6f3f;
  border: 1px solid #6f6f3f;

  &:hover {
    background-color: #808047;
    background: #808047;
    border: 1px solid #808047;
    opacity: 1;
    color: white;
  }
}

.custom-input {
  box-shadow: none;
}

.custom-hr {
  margin: 1rem auto;
  width: 90%;
}

.custom-textarea {
  // background: linear-gradient(135deg, #2e2e35 0%, #27272e 100%);
  background: rgb(47, 64, 91);
  border-radius: 14px;
  border: 1px solid rgba(204, 204, 204, 0.3137254902);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.04);
  border-radius: 14px;
  color: white;
  padding: 10px;
  font-size: 16px;
  resize: none;

  &:focus {
    background: rgb(47 64 91);
  }

  &:active {
    background: rgb(47 64 91);
  }
}

.custom-textarea::placeholder {
  color: #a0a0a0;
  opacity: 1;
}

strong {
  font-size: clamp(13px, 1vw, 15px);
  letter-spacing: -0.095em;
  text-transform: uppercase;
  text-align: left;
}

.reason-blocked-detail {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  place-content: start;
  height: -moz-fit-content;
  height: fit-content;
  place-items: flex-start;
  width: 100%;

  p {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    width: 100%;
  }
}

.text-primary-custom {
  color: rgb(96 165 250);
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.load-more-btn {
  position: sticky;
  bottom: 0;
  z-index: 1000;
  margin: 0;
  width: fit-content;
  padding: 0.45rem;
  text-align: center;
  width: 100%;
  background: #618ee1;
  color: white;
  font-weight: 600;
}

.send-msg-container {
  position: sticky;
  bottom: 0;
  z-index: 35;
  margin: 0;
  width: fit-content;
  text-align: center;
  width: 100%;
  // background: #61e176;
  color: white;
  font-weight: 600;
  display: flex;
  justify-content: space-between;
  align-items: center;
  white-space: nowrap;
  min-height: 40px;

  .custom-input {
    border-radius: 0px 0px 0px 0px !important;
    min-height: 40px;
  }

  .input-send-message {
    &.border-radius-none {
      border-radius: 0px !important ;
    }
  }

  .send-msg-btn {
    background: #27be3f;
    padding: 0.45rem;
    display: flex;
    align-items: center;
    text-align: center;
    justify-content: center;
    width: fit-content;
    border-radius: 0px 0px 0px 0px;
    color: rgb(255, 255, 255);
    font-weight: 600;
    min-height: 40px;
  }
}

.loader-wrapper {
  padding: 1rem 0rem;
  .loader {
    max-width: 35px;
    max-height: 35px;
  }
}

.filterBtn {
  cursor: pointer;
  &:hover {
    color: greenyellow;
  }
}

.refreshBtn {
  cursor: pointer;
  &:hover {
    color: greenyellow;
  }
}

.reason-note-explanation {
  text-align: start;
  display: flex;
  max-height: 70px;
  overflow-x: hidden;
  overflow-y: auto;
  letter-spacing: -0.075em;
}

.w-auto-dynamic {
  width: 60%;

  @media (max-width: 991px) {
    width: 100%;
  }
}

.select-msg-to-view {
  @media (max-width: 991px) {
    display: none;
  }
}

.close-button {
  @media (max-width: 991px) {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }

    span {
      position: absolute;
      width: 14px;
      height: 1px;
      background: rgba(255, 255, 255, 0.92);
      transform-origin: center;

      &:first-child {
        transform: rotate(45deg);
      }

      &:last-child {
        transform: rotate(-45deg);
      }
    }
  }
}

.border-radius-10 {
  border-radius: 10px;
}

.to-text {
  @media (max-width: 650px) {
    display: none;
  }
}

.live-counter-container {
  .live-player-counter {
    display: flex;
    justify-content: center;
    align-items: center;
    width: fit-content;
    background-color: #1976d2;
    flex-grow: 1;
    padding: 0px 8px;
    min-height: 40px;
    min-width: 75px;
  }
}

.outer-wrapper-no-msg {
  display: flex;
  height: 100%;
  min-height: 100%;
  align-items: center;
  flex-grow: 1;
}

.msg-sending {
  min-width: 60px;
  .loader {
    width: 100%;
    aspect-ratio: 5;
    --_g: no-repeat radial-gradient(circle closest-side, #ffffff 90%, #ffffff00);
    background: var(--_g) 0% 50%, var(--_g) 50% 50%, var(--_g) 100% 50%;
    background-size: calc(100% / 3) 100%;
    animation: l7 1s infinite linear;
  }
  @keyframes l7 {
    33% {
      background-size: calc(100% / 3) 0%, calc(100% / 3) 100%,
        calc(100% / 3) 100%;
    }
    50% {
      background-size: calc(100% / 3) 100%, calc(100% / 3) 0%,
        calc(100% / 3) 100%;
    }
    66% {
      background-size: calc(100% / 3) 100%, calc(100% / 3) 100%,
        calc(100% / 3) 0%;
    }
  }
}

.loader {
  width: 50px;
  aspect-ratio: 1;
  border-radius: 50%;
  background: radial-gradient(farthest-side, #ffffff 94%, #0000) top/8px 8px
      no-repeat,
    conic-gradient(#0000 30%, #ffffff);
  -webkit-mask: radial-gradient(farthest-side, #0000 calc(100% - 8px), #000 0);
  mask: radial-gradient(farthest-side, #0000 calc(100% - 8px), #000 0);
  animation: l13 1s infinite linear;
}
@keyframes l13 {
  100% {
    transform: rotate(1turn);
  }
}

.switch-btn-view {
  display: flex;
  align-items: center;
  border-radius: 0px;
  border: 2px solid #ffffff63;
  padding: 0px 5px;
  color: white;

  @media (max-width: 767px) {
    zoom: 80%;
  }
}

.active-nolive,
.active-live {
  background: #22c24b;
  color: white;
  font-weight: bold;
}

.active-nolive {
  margin: 0px 10px 0px 0px;
}

.flex-align {
  align-items: center;

  @media (max-width: 650px) {
    align-items: flex-start;
  }
}

.min-h-dyn-loader {
  min-height: 700px;

  @media (max-width: 767px) {
    min-height: 35vh;
  }
}

.cutted-userid {
  width: 100%;
  max-width: 40%;
  padding-right: 5px;
}
</style>
